import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { personaMeta } from "@narrator/registry";
import { PersonaPicker } from "../components/PersonaPicker";
import { PrimaryButton, SecondaryLink } from "../components/ui";
import { WorthyHumanBadge } from "../components/WorthyHumanBadge";
import { ResultsBoard } from "../components/voting/ResultsBoard";
import { useAccount } from "../context/AccountContext";
import { useRoomSocket } from "../hooks/useRoomSocket";
import {
	getPersonaPreference,
	type ResultsPersonaId,
	voterPathWithPersona,
} from "../lib/personaPreference";
import { pollPhaseLabel } from "../lib/phaseLabels";
import { friendlyApiErrorPayload } from "../lib/userFacingErrors";
import { webCuratorCopy } from "../lib/webPersonaCopy";
import { phaseFromLastWsMessage, resolveCuratorPhase } from "../lib/wsLive";
import { isFinalResults } from "../types/finalResults";

type CuratorResponse = {
	curator: true;
	source: string;
	d1: {
		roomId: string;
		question: string;
		votingType: string;
		phase: string;
		expiresAt: number;
		createdAt: number;
		finalizedAt: number | null;
		options: { id: number; content: string }[];
		finalResults?: unknown;
	};
	live: {
		roomId: string;
		question: string;
		votingType: string;
		phase: string;
		expiresAt: number;
		options: { id: number; content: string }[];
	} | null;
	capabilities: { canOpenVoting: boolean; canFinalize: boolean };
	wsPath: string;
};

type WsSnapshotPayload = NonNullable<CuratorResponse["live"]> & {
	capabilities?: { canOpenVoting: boolean; canFinalize: boolean };
};
type WsSnapshot = { type: "snapshot"; role: string; payload: WsSnapshotPayload };

const SUPPORT_PROMPT_DISMISS_KEY = "wi_seal_support_prompt_v1";

function displayShareUrl(path: string): string {
	if (typeof window === "undefined") return path;
	const full = `${window.location.origin}${path}`;
	return full.length > 56 ? `${full.slice(0, 28)}…${full.slice(-20)}` : full;
}

export function SealPage() {
	const { roomId, curatorToken } = useParams();
	const { tier } = useAccount();
	const showSupporterBadge = tier === "supporter" || tier === "team";
	const [showSupportPrompt, setShowSupportPrompt] = useState(
		() => typeof localStorage !== "undefined" && localStorage.getItem(SUPPORT_PROMPT_DISMISS_KEY) !== "1",
	);
	const [verifyErr, setVerifyErr] = useState<string | null>(null);
	const [verified, setVerified] = useState(false);
	const [curator, setCurator] = useState<CuratorResponse | null>(null);
	const [loadErr, setLoadErr] = useState<string | null>(null);
	const [finalizing, setFinalizing] = useState(false);
	const [openingVoting, setOpeningVoting] = useState(false);
	const [stickyPhase, setStickyPhase] = useState<string | null>(null);
	const [animateReveal, setAnimateReveal] = useState(false);
	const [personaId, setPersonaId] = useState<ResultsPersonaId>(() => getPersonaPreference());
	const [shareMsg, setShareMsg] = useState<string | null>(null);
	const curatorCopy = useMemo(() => webCuratorCopy(personaId, roomId ?? "web"), [personaId, roomId]);
	const lockedPersona = useMemo(() => personaMeta(personaId), [personaId]);
	const voterPath = roomId ? voterPathWithPersona(roomId, personaId) : "/";
	const inviteUrl = typeof window !== "undefined" ? `${window.location.origin}${voterPath}` : voterPath;

	useEffect(() => {
		const meta = document.createElement("meta");
		meta.name = "referrer";
		meta.content = "no-referrer";
		document.head.appendChild(meta);
		return () => {
			meta.remove();
		};
	}, []);

	const { connected, upgradeRejected, lastEvent, send } = useRoomSocket(roomId, "curator", verified && Boolean(roomId));

	const refetchCurator = useCallback(async (): Promise<CuratorResponse | null> => {
		if (!roomId) return null;
		const cg = await fetch(`/api/rooms/${encodeURIComponent(roomId)}/curator`, { credentials: "include" });
		if (!cg.ok) return null;
		const cdata = (await cg.json()) as CuratorResponse;
		setCurator(cdata);
		const nextPhase = cdata.live?.phase ?? cdata.d1.phase;
		if (nextPhase) setStickyPhase(nextPhase);
		return cdata;
	}, [roomId]);

	/** D1 finalize can lag WS phase_change — poll until results land (also covers flaky WS). */
	const pollCuratorAfterFinalize = useCallback(async () => {
		for (let attempt = 0; attempt < 8; attempt++) {
			if (attempt > 0) await new Promise((r) => window.setTimeout(r, 400));
			const cdata = await refetchCurator();
			if (cdata?.d1.phase === "revealed" && isFinalResults(cdata.d1.finalResults)) return;
		}
	}, [refetchCurator]);

	useEffect(() => {
		if (!lastEvent || typeof lastEvent !== "object") return;
		const next = phaseFromLastWsMessage(lastEvent);
		if (!next) return;
		setStickyPhase(next);
		if (next === "voting" || next === "revealed") {
			void refetchCurator();
		}
		if (next === "revealed") {
			// Animate only on live phase_change — never on cold-load snapshot.
			if ((lastEvent as { type?: string }).type === "phase_change") {
				setAnimateReveal(true);
			}
			const retry = window.setTimeout(() => void refetchCurator(), 800);
			return () => window.clearTimeout(retry);
		}
	}, [lastEvent, refetchCurator]);

	useEffect(() => {
		if (!connected || !roomId || !verified) return;
		const t = window.setInterval(() => {
			send({ type: "presence_ping" });
		}, 28_000);
		return () => window.clearInterval(t);
	}, [connected, roomId, verified, send]);

	useEffect(() => {
		if (!roomId || !curatorToken) return;
		let cancelled = false;
		(async () => {
			setVerifyErr(null);
			const res = await fetch(`/api/rooms/${encodeURIComponent(roomId)}/curator/verify`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				credentials: "include",
				body: JSON.stringify({ seal: curatorToken.toLowerCase() }),
			});
			const data = (await res.json()) as { error?: string };
			if (!res.ok) {
				if (!cancelled)
					setVerifyErr(friendlyApiErrorPayload(data as unknown, "We couldn’t verify this Curator Link. Check for typos in the URL."));
				return;
			}
			if (!cancelled) setVerified(true);
			const cg = await fetch(`/api/rooms/${encodeURIComponent(roomId)}/curator`, { credentials: "include" });
			const cdata = (await cg.json()) as CuratorResponse & { error?: string };
			if (!cg.ok) {
				if (!cancelled)
					setLoadErr(
						friendlyApiErrorPayload(cdata as unknown, "We couldn’t load the curator view. Try refreshing."),
					);
				return;
			}
			if (!cancelled) {
				setCurator(cdata as CuratorResponse);
				const seedPhase = cdata.live?.phase ?? cdata.d1.phase;
				if (seedPhase) setStickyPhase(seedPhase);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [roomId, curatorToken]);

	const copyInvite = useCallback(async () => {
		try {
			await navigator.clipboard.writeText(inviteUrl);
			setShareMsg("Invitation link copied");
			window.setTimeout(() => setShareMsg(null), 2400);
		} catch {
			setShareMsg("Couldn’t copy — select the link and copy manually");
			window.setTimeout(() => setShareMsg(null), 3200);
		}
	}, [inviteUrl]);

	const shareInvite = useCallback(async () => {
		if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
			try {
				await navigator.share({
					title: curator?.d1.question ?? "WorthyIdea poll",
					text: "You’re invited to vote on this WorthyIdea poll.",
					url: inviteUrl,
				});
				setShareMsg("Shared");
				window.setTimeout(() => setShareMsg(null), 2400);
				return;
			} catch (err) {
				if (err instanceof DOMException && err.name === "AbortError") return;
			}
		}
		await copyInvite();
	}, [copyInvite, curator?.d1.question, inviteUrl]);

	if (!roomId || !curatorToken) {
		return <p className="text-wi-text/70">This curator link looks incomplete. Use the full link from when you created the poll.</p>;
	}

	if (verifyErr) {
		return (
			<div className="space-y-md">
				<h1 className="font-display text-h1 text-wi-text">Curator</h1>
				<p className="text-wi-gold" role="alert">
					{verifyErr}
				</p>
				<SecondaryLink to="/">Back home</SecondaryLink>
			</div>
		);
	}

	if (verified && !curator) {
		if (loadErr) {
			return (
				<div className="space-y-md">
					<h1 className="font-display text-h1 text-wi-text">Curator</h1>
					<p className="text-wi-gold" role="alert">
						{loadErr}
					</p>
					<SecondaryLink to="/">Back home</SecondaryLink>
				</div>
			);
		}
		return (
			<p className="text-wi-text/60" role="status" aria-live="polite">
				Loading curator tools…
			</p>
		);
	}

	if (!verified) {
		return (
			<p className="text-wi-text/60" role="status" aria-live="polite">
				Verifying curator access…
			</p>
		);
	}

	const fromWs =
		lastEvent && typeof lastEvent === "object" && lastEvent !== null && (lastEvent as { type?: string }).type === "snapshot"
			? (lastEvent as WsSnapshot).payload
			: null;

	const live = fromWs ?? curator!.live ?? curator!.d1;
	const phase = resolveCuratorPhase({
		lastEvent,
		stickyPhase,
		httpPhase: curator!.live?.phase ?? curator!.d1.phase,
	});
	const wsCaps = fromWs?.capabilities;
	const canOpenVoting =
		typeof wsCaps?.canOpenVoting === "boolean" ? wsCaps.canOpenVoting : phase === "collecting";
	const canFinalize =
		typeof wsCaps?.canFinalize === "boolean" ? wsCaps.canFinalize : phase === "voting";

	const results = isFinalResults(curator!.d1.finalResults) ? curator!.d1.finalResults : null;
	const showResults = (phase === "revealed" || curator!.d1.phase === "revealed") && results;
	const votingOpen = phase === "voting" || phase === "revealed";

	return (
		<div className="space-y-lg">
			<div className="flex flex-wrap items-start justify-between gap-md">
				<div>
					<p className="font-medium text-caption text-wi-gold uppercase tracking-[0.2em]">Curator</p>
					<h1 className="font-display text-h1 text-wi-text">{live.question}</h1>
				</div>
				<div className="flex flex-col items-end gap-1.5">
					{showSupporterBadge ? <WorthyHumanBadge /> : null}
					<span className={`text-caption font-medium ${connected ? "text-wi-signal" : "text-wi-text/55"}`}>
						{connected ? "Live" : "Connecting…"}
					</span>
					{upgradeRejected ? (
						<p className="max-w-xs text-right text-wi-gold/90 text-caption leading-snug">
							Live channel did not open. Re-open your Curator Link or refresh — your controls may be momentarily stale.
						</p>
					) : null}
					<span className="text-caption font-medium text-wi-text/72" role="status" aria-live="polite">
						{pollPhaseLabel(phase)}
					</span>
				</div>
			</div>

			{phase === "collecting" ? (
				<PersonaPicker
					value={personaId}
					onChange={setPersonaId}
					hint="Sets character language on the shared voter link (?persona=). Locked after you launch voting. Recap voice uses the persona stored on the room when created."
				/>
			) : (
				<div
					className="rounded-xl border border-wi-border bg-wi-surface/70 px-4 py-3"
					role="status"
					aria-label={`Character language locked: ${lockedPersona.label}`}
				>
					<p className="font-medium text-wi-text-muted text-xs uppercase tracking-wide">Character language</p>
					<p className="mt-1.5 font-medium text-sm text-wi-text">{lockedPersona.label}</p>
					<p className="mt-0.5 text-wi-text-muted text-xs leading-snug">{lockedPersona.description}</p>
					<p className="mt-1.5 text-wi-text-muted text-xs leading-snug">Locked for this poll — set before launch.</p>
				</div>
			)}

			{/* Two-state control: one PrimaryButton at a time (Launch ↔ Finalize) */}
			<div className="wi-cta-row flex flex-col items-start gap-sm">
				{phase === "collecting" ? (
					<>
						<PrimaryButton
							disabled={!canOpenVoting || !connected || openingVoting}
							onClick={() => {
								setOpeningVoting(true);
								setStickyPhase("voting");
								send({ type: "open_voting" });
								void refetchCurator().finally(() => setOpeningVoting(false));
							}}
						>
							{openingVoting ? "Opening…" : curatorCopy.openVotingLabel}
						</PrimaryButton>
						<p className="max-w-xl text-body-sm text-wi-text/70 leading-relaxed">
							Voters cannot cast until you launch. Open voting first, then share the invitation link below.
						</p>
					</>
				) : null}
				{phase === "voting" ? (
					<>
						<PrimaryButton
							disabled={!canFinalize || !connected || finalizing}
							onClick={() => {
								setAnimateReveal(true);
								setStickyPhase("revealed");
								send({ type: "finalize_decision" });
								setFinalizing(true);
								void pollCuratorAfterFinalize().finally(() => setFinalizing(false));
							}}
						>
							{finalizing ? "Finalizing…" : curatorCopy.finalizeLabel}
						</PrimaryButton>
						{!openingVoting ? (
							<span className="text-body-sm text-emerald-900" role="status" aria-live="polite">
								{curatorCopy.votingOpenStatus}
							</span>
						) : null}
					</>
				) : null}
			</div>

			{votingOpen ? (
				<section
					className="space-y-sm rounded-xl border border-wi-gold/25 bg-wi-surface/80 p-4 shadow-wi-soft"
					aria-labelledby="wi-seal-share-heading"
				>
					<h2 id="wi-seal-share-heading" className="font-medium text-body-sm text-wi-text">
						Share invitation
					</h2>
					<p className="text-body-sm text-wi-text/72 leading-relaxed">
						Send this link to voters — never your Curator Link.
					</p>
					<div className="flex flex-wrap gap-2">
						<code
							className="max-w-full min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap rounded-xl border border-wi-border bg-wi-bg px-3 py-2.5 text-caption leading-relaxed"
							title={inviteUrl}
						>
							{displayShareUrl(voterPath)}
						</code>
						<button
							type="button"
							className="min-h-11 rounded-xl border border-wi-border px-5 py-3 text-body-sm transition hover:border-wi-gold/35 hover:bg-wi-bg/50"
							onClick={() => void copyInvite()}
						>
							Copy link
						</button>
						<button
							type="button"
							className="min-h-11 rounded-xl border border-wi-gold/35 bg-wi-gold/10 px-5 py-3 font-medium text-body-sm text-wi-text transition hover:bg-wi-gold/15"
							onClick={() => void shareInvite()}
						>
							Share
						</button>
					</div>
					{shareMsg ? (
						<p className="text-caption text-wi-signal" role="status" aria-live="polite">
							{shareMsg}
						</p>
					) : null}
					<p className="text-caption text-wi-text/55">
						Preview:{" "}
						<Link className="font-medium text-wi-gold underline-offset-2 hover:underline" to={voterPath}>
							open voter page
						</Link>
					</p>
				</section>
			) : (
				<p className="text-body-sm text-wi-text/65 leading-relaxed">
					Share tools appear here after you launch voting. Keep this Curator page private.
				</p>
			)}

			{showResults ? <ResultsBoard results={results} animateReveal={animateReveal} /> : null}

			{showSupportPrompt ? (
				<div className="flex flex-wrap items-center justify-between gap-sm border-wi-text/[0.08] border-t pt-sm text-caption text-wi-text/55">
					<p className="leading-relaxed">
						<Link to="/membership" className="font-medium text-wi-gold underline-offset-2 hover:underline">
							Membership
						</Link>{" "}
						adds stewardship tools when they ship.
					</p>
					<button
						type="button"
						className="shrink-0 text-wi-text/40 transition-colors duration-wi-hover ease-in-out hover:text-wi-text/70"
						onClick={() => {
							try {
								localStorage.setItem(SUPPORT_PROMPT_DISMISS_KEY, "1");
							} catch {
								/* ignore */
							}
							setShowSupportPrompt(false);
						}}
					>
						Dismiss
					</button>
				</div>
			) : null}

			<SecondaryLink to="/" arrow={false}>
				← New poll
			</SecondaryLink>
		</div>
	);
}
